/**
 * The screen registry — split out of `App.tsx` (the B4 nav split the launch
 * plan calls for) so a wave adding a panel page touches THIS file and its own
 * route component, never the shell. `App.tsx` was clobbered twice during the
 * B2/B3 marathon because every wave had to co-edit it; the shell now imports
 * the registry and renders whatever is here.
 *
 * The navigation is grouped rather than listed. Twenty equal links is a list an
 * operator re-reads every time; named groups is a structure they learn once.
 * The two queues carry a live count of what is waiting, because "is anything
 * waiting on me" is the question a shift starts and ends with, and it should
 * be answerable without opening a screen.
 *
 * The groups answer "what am I looking after", not "which service is this":
 *
 *   Overview     the readings — is the business behaving, is anything waiting
 *   Customers    the people with accounts: PLAYERS, and the OPERATORS who serve
 *                them. Both are rosters of accounts with a status and a history,
 *                and both are managed the same way, so they sit together.
 *   Money desk   the queues where money actually moves
 *   Catalogue    what a player is offered — games and the lobby that shows them
 *   Growth       what is given away, and to whom
 *   Platform     the panel's own plumbing: the log and the keys
 */
import type { ReactNode } from 'react';
import {
  IconDie,
  IconGauge,
  IconInbound,
  IconLock,
  IconOutbound,
  IconPeople,
  IconPlug,
  IconSearch,
  IconPulse,
  IconShield,
  IconSliders,
  IconTrend,
  IconVault,
} from './ui/icons.tsx';
import { Analytics } from './routes/Analytics.tsx';
import { Ask } from './routes/Ask.tsx';
import { AiOps } from './routes/AiOps.tsx';
import { Audit } from './routes/Audit.tsx';
import { Dashboard } from './routes/Dashboard.tsx';
import { Deposits } from './routes/Deposits.tsx';
import { Withdrawals } from './routes/Withdrawals.tsx';
import { Players } from './routes/Players.tsx';
import { Operators } from './routes/Operators.tsx';
import { PasswordResets } from './routes/PasswordResets.tsx';
import { Support } from './routes/Support.tsx';
import { Games } from './routes/Games.tsx';
import { Lobby } from './routes/Lobby.tsx';
import { Promotions } from './routes/Promotions.tsx';
import { PaymentMethods } from './routes/PaymentMethods.tsx';
import { Reconciliation } from './routes/Reconciliation.tsx';
import { Reports } from './routes/Reports.tsx';
import { Segments } from './routes/Segments.tsx';
import { Races } from './routes/Races.tsx';
import { Secrets } from './routes/Secrets.tsx';

export interface Screen {
  id: string;
  label: string;
  title: string;
  /** One line saying what this screen is for. Shown under the title. */
  sub: string;
  icon: ReactNode;
  /** Which pending counter, if any, belongs on this link. */
  queue?: 'deposits' | 'withdrawals';
  /**
   * Minimum role for the link to appear. The SERVER refuses the request
   * regardless — this only stops showing a screen that would 403 on every call.
   * Hiding is not the control; `requireOperator` is.
   */
  minRole?: 'viewer' | 'approver' | 'admin';
  render: () => ReactNode;
}

export interface NavGroup {
  label: string;
  screens: Screen[];
}

export const NAV: NavGroup[] = [
  {
    label: 'Overview',
    screens: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        title: 'Dashboard',
        sub: 'Is money behaving, and is anything waiting on you',
        icon: <IconGauge />,
        render: () => <Dashboard />,
      },
      {
        id: 'analytics',
        label: 'Analytics',
        title: 'Analytics',
        sub: 'The business over time — daily series, funnel, retention, lifetime P&L',
        icon: <IconTrend />,
        render: () => <Analytics />,
      },
      {
        id: 'reports',
        label: 'Reports',
        title: 'Reports',
        sub: 'The honest NGR — GGR less bonus cost, modelled rail fees, the cost drawer and affiliate — plus exposure and the payout SLA',
        icon: <IconTrend />,
        render: () => <Reports />,
      },
      {
        id: 'ask',
        label: 'Ask',
        title: 'Ask your data',
        sub: 'A plain-words question, one read-only query over the masked views, the SQL always shown',
        icon: <IconSearch />,
        // Admin only — the server gates POST /internal/admin/ai/query on
        // requireOperator('admin') (ADR-057); hiding the link just stops the 403.
        minRole: 'admin',
        render: () => <Ask />,
      },
      {
        id: 'ai-ops',
        label: 'AI ops',
        title: 'AI ops',
        sub: 'Agent switches, the runs audit, risk alerts, proposals and the executive digest — internal, advisory only',
        icon: <IconPulse />,
        minRole: 'viewer',
        render: () => <AiOps />,
      },
    ],
  },
  {
    label: 'Money desk',
    screens: [
      {
        id: 'deposits',
        label: 'Deposits',
        title: 'Deposit approvals',
        sub: 'Match a bank UTR to a reference and credit the player',
        icon: <IconInbound />,
        queue: 'deposits',
        render: () => <Deposits />,
      },
      {
        id: 'withdrawals',
        label: 'Withdrawals',
        title: 'Withdrawal approvals',
        sub: 'Sign off a payout, then record that it actually left the bank',
        icon: <IconOutbound />,
        queue: 'withdrawals',
        render: () => <Withdrawals />,
      },
      {
        id: 'methods',
        label: 'Payment methods',
        title: 'Collection registry',
        sub: 'The accounts deposits are collected into, and how much room is left',
        icon: <IconVault />,
        render: () => <PaymentMethods />,
      },
      {
        id: 'reconciliation',
        label: 'Reconciliation',
        title: 'UPI reconciliation',
        sub: 'The bank statement against the deposit queue. Matches are proposals — crediting stays on Deposits',
        icon: <IconPlug />,
        render: () => <Reconciliation />,
      },
    ],
  },
  {
    label: 'Customers',
    screens: [
      {
        id: 'players',
        label: 'Players',
        title: 'Players',
        sub: 'Balance, ledger statement and account status',
        icon: <IconPeople />,
        render: () => <Players />,
      },
      {
        id: 'operators',
        label: 'Operators',
        title: 'Operators',
        sub: 'Who can sign into this panel, and with what power. Create an account, change a role, suspend access, force a password reset',
        icon: <IconShield />,
        // Admin only — every route the screen calls is `requireOperator('admin')`
        // on the server, including the read. Hiding the link stops a viewer
        // landing on a screen that 403s before it renders a row.
        minRole: 'admin',
        render: () => <Operators />,
      },
      {
        id: 'support',
        label: 'Support',
        title: 'Support',
        sub: 'The ticket queue — filter, assign to yourself, and reply to a thread',
        icon: <IconInbound />,
        render: () => <Support />,
      },
      {
        id: 'password-resets',
        label: 'Password resets',
        title: 'Password resets',
        sub: 'Recovery requests from players who cannot sign in. Verify the person, then issue a one-time link',
        icon: <IconLock />,
        // Reading is viewer; ISSUING is approver and audited server-side. The
        // link is left visible to a viewer on purpose — seeing who is locked
        // out is half the job, and the issue button disables itself.
        minRole: 'viewer',
        render: () => <PasswordResets />,
      },
    ],
  },
  {
    label: 'Catalogue',
    screens: [
      {
        id: 'games',
        label: 'Games',
        title: 'Games',
        sub: 'Availability, stake limits, and the house edge (RTP). Seated bot skill lives here too',
        icon: <IconDie />,
        render: () => <Games />,
      },
      {
        id: 'lobby',
        label: 'Lobby',
        title: 'Lobby layout',
        sub: 'What the client lobby shows — rails, ordering and badges. Served from one audited setting',
        icon: <IconSliders />,
        // Admin only — PUT /internal/admin/settings/lobby is requireOperator('admin').
        minRole: 'admin',
        render: () => <Lobby />,
      },
    ],
  },
  {
    label: 'Growth',
    screens: [
      {
        id: 'promotions',
        label: 'Promotions',
        title: 'Promotions',
        sub: 'Generosity — deposit match, wagering, VIP rakeback, game weights, bonus liability',
        icon: <IconTrend />,
        // Admin only — every promo/VIP write is gated `requireOperator('admin')`
        // server-side; hiding the link just stops a viewer's guaranteed 403.
        minRole: 'admin',
        render: () => <Promotions />,
      },
      {
        id: 'races',
        label: 'Races',
        title: 'Wager races',
        sub: 'Leaderboards over real wagering — create, edit while scheduled, void, and audit the paid standings',
        icon: <IconTrend />,
        // Admin only — every race write is requireOperator('admin') server-side.
        minRole: 'admin',
        render: () => <Races />,
      },
      {
        id: 'segments',
        label: 'Segments',
        title: 'Segments',
        sub: 'Saved player queries over a whitelisted field map — preview, CSV export, batch bonus grant through the promo service',
        icon: <IconSearch />,
        // Admin only — segment writes are requireOperator('admin') server-side
        // (the grant itself is approver); hiding the link stops a viewer's 403.
        minRole: 'admin',
        render: () => <Segments />,
      },
    ],
  },
  {
    label: 'Platform',
    screens: [
      {
        id: 'audit',
        label: 'Audit log',
        title: 'Operator audit log',
        sub: 'Append-only. Who did what, when, to whom, and why',
        icon: <IconPulse />,
        render: () => <Audit />,
      },
      {
        id: 'keys',
        label: 'Keys',
        title: 'Platform keys',
        sub: 'Stored encrypted, rotated here, never shown again',
        icon: <IconShield />,
        // Admin only. The server gates every route in the key manager on
        // `requireOperator('admin')` — reads included — so a viewer would get a
        // 403 on load. Hiding the link stops that being a mystery.
        minRole: 'admin',
        render: () => <Secrets />,
      },
    ],
  },
];

export const SCREENS: Screen[] = NAV.flatMap((group) => group.screens);

/** Least privilege, ascending — the same ranking the server enforces. */
const RANK: Record<string, number> = { viewer: 1, approver: 2, admin: 3 };

/**
 * The screens a role may see. **This is presentation, not access control.**
 * Every route the panel calls is gated on the server by `requireOperator`, and
 * a viewer who types the URL still gets a 403 from the API. Hiding a link the
 * operator can only be refused from is a courtesy; it is not the mechanism.
 */
export function visibleTo(group: NavGroup, role: string | undefined): Screen[] {
  return group.screens.filter(
    (s) => !s.minRole || (RANK[role ?? 'viewer'] ?? 0) >= RANK[s.minRole]!,
  );
}
